import type { ResourceCategoryKey } from '@/features/virtual-resource/constants'
import DataFileMap from '@/model/Resource/DataFileMap'
import {
  getResourceCategoryRule,
  resolveEnginePathFromRelativePath,
  resolveRelativePathFromEnginePath,
  getTopFolderRuleBySegment,
  getTopFolderRuleByMappingPath,
  resolveRelativePathFromVirtualPath,
  resolveVirtualPathFromRelativePath,
} from '@/features/virtual-resource/rules'

export type FileNamePathMode = 'relative' | 'mapped' | 'engine'

type ResourceFileMeta = {
  relativePath: string
  mappingPath: string
  virtualPath: string
  storedPath: string
  enginePath: string
  resourceUrl: string
  name: string
  displayName: string
  node: DataFileMap
}

function normalizePath(value: string): string {
  return String(value || '')
    .trim()
    .replace(/\\/g, '/')
    .replace(/^\/+|\/+$/g, '')
}

function joinSegments(...segments: Array<string | null | undefined>): string {
  return segments
    .map((item) => normalizePath(item || ''))
    .filter(Boolean)
    .join('/')
}

const CATEGORY_LABEL_ALIASES: Partial<Record<ResourceCategoryKey, string[]>> = {
  graphic: ['图像', '图片'],
  audio: ['音频'],
  video: ['视频'],
  font: ['字体'],
}

type BaseFolderMeta = {
  folderPath: string
  mappingPath: string
  relativePrefix: string
}

function startsWithPath(path: string, prefix: string): boolean {
  const normalizedPath = normalizePath(path).toLowerCase()
  const normalizedPrefix = normalizePath(prefix).toLowerCase()
  if (!normalizedPrefix) return true
  return normalizedPath === normalizedPrefix || normalizedPath.startsWith(`${normalizedPrefix}/`)
}

function stripCategoryLabelPrefix(category: ResourceCategoryKey, path: string): string {
  const normalized = normalizePath(path)
  if (!normalized) return ''
  const segments = normalized.split('/').filter(Boolean)
  if (!segments.length) return ''
  const labelAliases = [getResourceCategoryRule(category).label, ...(CATEGORY_LABEL_ALIASES[category] ?? [])].map(
    (item) => normalizePath(item).toLowerCase(),
  )
  if (labelAliases.includes(segments[0]!.toLowerCase())) {
    segments.shift()
  }
  return joinSegments(...segments)
}

export function normalizeResourceBaseFolderPath(category: ResourceCategoryKey, folderPath: string): string {
  const normalized = stripCategoryLabelPrefix(category, folderPath)
  if (!normalized) return ''
  const segments = normalized.split('/').filter(Boolean)
  if (!segments.length) return ''
  const labelAliases = [getResourceCategoryRule(category).label, ...(CATEGORY_LABEL_ALIASES[category] ?? [])].map(
    (item) => normalizePath(item).toLowerCase(),
  )
  if (labelAliases.includes(segments[0]!.toLowerCase())) {
    segments.shift()
  }
  if (!segments.length) return ''
  const topFolder = getTopFolderRuleBySegment(category, segments[0] ?? '')
  if (topFolder) {
    segments[0] = topFolder.name
  }
  return joinSegments(...segments)
}

export function resolveFullDisplayPathFromFileName(
  root: DataFileMap | null | undefined,
  category: ResourceCategoryKey,
  storedPath: string,
  mode: FileNamePathMode,
  baseFolderPath?: string,
): string {
  const display = resolveDisplayPathFromFileName(root, category, storedPath, mode, baseFolderPath)
  if (!display) return ''
  return joinSegments(getResourceCategoryRule(category).label, display)
}

function resolveBaseFolderMeta(category: ResourceCategoryKey, baseFolderPath?: string): BaseFolderMeta | null {
  const normalized = normalizeResourceBaseFolderPath(category, baseFolderPath ?? '')
  if (!normalized) return null
  const segments = normalized.split('/').filter(Boolean)
  const topFolder = getTopFolderRuleBySegment(category, segments[0] ?? '')
  if (!topFolder) return null
  return {
    folderPath: joinSegments(topFolder.name, ...segments.slice(1)),
    mappingPath: normalizePath(topFolder.mappingPath),
    relativePrefix: joinSegments(...segments.slice(1)),
  }
}

function isMetaWithinBase(meta: ResourceFileMeta, base: BaseFolderMeta | null): boolean {
  if (!base) return true
  if (normalizePath(meta.mappingPath).toLowerCase() !== base.mappingPath.toLowerCase()) return false
  return startsWithPath(meta.relativePath, base.relativePrefix)
}

function getRelativePathWithinBase(meta: ResourceFileMeta, base: BaseFolderMeta | null): string {
  const normalized = normalizePath(meta.relativePath)
  if (!base) return normalized
  const prefix = normalizePath(base.relativePrefix)
  if (!prefix) return normalized
  if (normalized.toLowerCase() === prefix.toLowerCase()) return ''
  if (normalized.toLowerCase().startsWith(`${prefix.toLowerCase()}/`)) {
    return normalized.slice(prefix.length + 1)
  }
  return normalized
}

function getVirtualPathWithinBase(meta: ResourceFileMeta, base: BaseFolderMeta | null): string {
  const relative = getRelativePathWithinBase(meta, base)
  if (!base) return normalizePath(meta.virtualPath)
  return joinSegments(base.folderPath, relative)
}

function collectCandidateFiles(
  root: DataFileMap | null | undefined,
  category: ResourceCategoryKey,
  baseFolderPath?: string,
): { files: ResourceFileMeta[]; base: BaseFolderMeta | null } {
  const base = resolveBaseFolderMeta(category, baseFolderPath)
  const files = collectResourceFiles(root, category).filter((item) => isMetaWithinBase(item, base))
  return { files, base }
}

function collectResourceFiles(root: DataFileMap | null | undefined, category: ResourceCategoryKey): ResourceFileMeta[] {
  if (!root) return []
  const out: ResourceFileMeta[] = []
  const stack: DataFileMap[] = [root]
  while (stack.length) {
    const node = stack.pop()!
    if (node.isFolder) {
      for (let index = node.children.length - 1; index >= 0; index -= 1) {
        stack.push(node.children[index]!)
      }
      continue
    }
    const relativePath = normalizePath(node.relativePath || node.name)
    const mappingPath = normalizePath(node.mappingPath)
    const storedPath = joinSegments(mappingPath, relativePath) || relativePath
    const enginePath = normalizePath(resolveEnginePathFromRelativePath(category, mappingPath, relativePath))
    const virtualPath = normalizePath(resolveVirtualPathFromRelativePath(category, mappingPath, relativePath))
    out.push({
      relativePath,
      mappingPath,
      storedPath,
      enginePath,
      virtualPath,
      resourceUrl: node.resourceUrl,
      name: node.name,
      displayName: String(node.links[2] || '').trim(),
      node,
    })
  }
  return out
}

function parseEnginePath(
  category: ResourceCategoryKey,
  enginePath: string,
): { mappingPath: string; relativePath: string; virtualPath: string } | null {
  const normalized = normalizePath(enginePath)
  if (!normalized) return null
  const parsed = resolveRelativePathFromEnginePath(category, normalized)
  if (!parsed.mappingPath) return null
  return {
    mappingPath: normalizePath(parsed.mappingPath),
    relativePath: normalizePath(parsed.relativePath),
    virtualPath: normalizePath(resolveVirtualPathFromRelativePath(category, parsed.mappingPath, parsed.relativePath)),
  }
}

function parseMappedStoredPath(
  category: ResourceCategoryKey,
  storedPath: string,
): { mappingPath: string; relativePath: string; virtualPath: string } | null {
  const normalized = normalizePath(storedPath)
  if (!normalized) return null
  const segments = normalized.split('/').filter(Boolean)
  for (let length = Math.min(2, segments.length); length >= 1; length -= 1) {
    const mappingPath = segments.slice(0, length).join('/')
    const rule = getTopFolderRuleByMappingPath(category, mappingPath)
    if (!rule) continue
    const relativePath = segments.slice(length).join('/')
    return {
      mappingPath,
      relativePath,
      virtualPath: normalizePath(resolveVirtualPathFromRelativePath(category, mappingPath, relativePath)),
    }
  }
  return null
}

export function findResourceFileMetaByStoredPath(
  root: DataFileMap | null | undefined,
  category: ResourceCategoryKey,
  storedPath: string,
  baseFolderPath?: string,
): ResourceFileMeta | null {
  const normalized = normalizePath(storedPath)
  if (!normalized) return null
  const { files, base } = collectCandidateFiles(root, category, baseFolderPath)
  const stripped = stripCategoryLabelPrefix(category, normalized)
  const candidates = stripped && stripped !== normalized ? [normalized, stripped] : [normalized]
  for (const target of candidates) {
    const hit =
      files.find((item) => getRelativePathWithinBase(item, base).toLowerCase() === target.toLowerCase()) ??
      files.find((item) => normalizePath(item.storedPath).toLowerCase() === target.toLowerCase()) ??
      files.find((item) => normalizePath(item.enginePath).toLowerCase() === target.toLowerCase()) ??
      files.find((item) => normalizePath(item.relativePath).toLowerCase() === target.toLowerCase()) ??
      files.find((item) => normalizePath(getVirtualPathWithinBase(item, base)).toLowerCase() === target.toLowerCase()) ??
      files.find((item) => normalizePath(item.virtualPath).toLowerCase() === target.toLowerCase()) ??
      null
    if (hit) return hit
  }
  return (
    candidates
      .map((target) => parseMappedStoredPath(category, target))
      .filter((item): item is NonNullable<ReturnType<typeof parseMappedStoredPath>> => item !== null)
      .map((item) => files.find((file) =>
        normalizePath(file.mappingPath).toLowerCase() === item.mappingPath.toLowerCase()
        && normalizePath(file.relativePath).toLowerCase() === item.relativePath.toLowerCase(),
      ))
      .find((item): item is ResourceFileMeta => item !== undefined) ??
    null
  )
}

/**
 * 按保存路径查找资源树中的原始文件节点。
 * @param root 资源树根节点。
 * @param category 资源分类。
 * @param storedPath 数据结构中保存的资源路径。
 * @param baseFolderPath 基准目录，例如 `图片/UI`。
 * @returns 命中的资源节点；未命中时返回 `null`。
 */
export function findResourceFileNodeByStoredPath(
  root: DataFileMap | null | undefined,
  category: ResourceCategoryKey,
  storedPath: string,
  baseFolderPath?: string,
): DataFileMap | null {
  return findResourceFileMetaByStoredPath(root, category, storedPath, baseFolderPath)?.node ?? null
}

export function resolveDisplayPathFromFileName(
  root: DataFileMap | null | undefined,
  category: ResourceCategoryKey,
  storedPath: string,
  mode: FileNamePathMode,
  baseFolderPath?: string,
): string {
  const normalized = normalizePath(storedPath)
  if (!normalized) return ''
  const meta = findResourceFileMetaByStoredPath(root, category, normalized, baseFolderPath)
  const base = resolveBaseFolderMeta(category, baseFolderPath)
  if (meta) {
    const relativeWithinBase = getRelativePathWithinBase(meta, base)
    if (mode === 'mapped' || mode === 'engine') {
      return getVirtualPathWithinBase(meta, base) || relativeWithinBase || meta.relativePath
    }
    return relativeWithinBase || meta.relativePath
  }
  if (mode === 'mapped' || mode === 'engine') {
    const parsed = parseMappedStoredPath(category, normalized)
    if (parsed) {
      if (!base) return parsed.virtualPath || parsed.relativePath
      const relativeWithinBase = getRelativePathWithinBase(
        {
          relativePath: parsed.relativePath,
          mappingPath: parsed.mappingPath,
          virtualPath: parsed.virtualPath,
          storedPath: normalized,
          enginePath: normalizePath(resolveEnginePathFromRelativePath(category, parsed.mappingPath, parsed.relativePath)),
          resourceUrl: '',
          name: '',
          displayName: '',
          node: new DataFileMap(),
        },
        base,
      )
      return joinSegments(base.folderPath, relativeWithinBase || parsed.relativePath)
    }
    const parsedEngine = parseEnginePath(category, normalized)
    if (parsedEngine) {
      if (!base) return parsedEngine.virtualPath || parsedEngine.relativePath
      const relativeWithinBase = getRelativePathWithinBase(
        {
          relativePath: parsedEngine.relativePath,
          mappingPath: parsedEngine.mappingPath,
          virtualPath: parsedEngine.virtualPath,
          storedPath: joinSegments(parsedEngine.mappingPath, parsedEngine.relativePath),
          enginePath: normalized,
          resourceUrl: '',
          name: '',
          displayName: '',
          node: new DataFileMap(),
        },
        base,
      )
      return joinSegments(base.folderPath, relativeWithinBase || parsedEngine.relativePath)
    }
    if (base) return joinSegments(base.folderPath, normalized)
  }
  return normalized
}

export function resolveStoredPathFromDisplayPath(
  root: DataFileMap | null | undefined,
  category: ResourceCategoryKey,
  displayPath: string,
  mode: FileNamePathMode,
  baseFolderPath?: string,
): string {
  const normalized = stripCategoryLabelPrefix(category, displayPath)
  if (!normalized) return ''
  const { files, base } = collectCandidateFiles(root, category, baseFolderPath)
  const toStoredPath = (relativePath: string, mappingPath: string): string =>
    joinSegments(mappingPath, relativePath) || normalizePath(relativePath)
  const toEnginePath = (relativePath: string, mappingPath: string): string =>
    normalizePath(resolveEnginePathFromRelativePath(category, mappingPath, relativePath))
  const exact = files.find((item) => {
    const relativeWithinBase = getRelativePathWithinBase(item, base)
    const virtualWithinBase = getVirtualPathWithinBase(item, base)
    if (mode === 'mapped' || mode === 'engine') {
      return (
        normalizePath(virtualWithinBase).toLowerCase() === normalized.toLowerCase()
        || normalizePath(relativeWithinBase).toLowerCase() === normalized.toLowerCase()
      )
    }
    return normalizePath(relativeWithinBase).toLowerCase() === normalized.toLowerCase()
  })
  if (exact) {
    if (mode === 'mapped') {
      return normalizePath(exact.storedPath) || toStoredPath(exact.relativePath, exact.mappingPath)
    }
    if (mode === 'engine') {
      return normalizePath(exact.enginePath) || toEnginePath(exact.relativePath, exact.mappingPath)
    }
    return getRelativePathWithinBase(exact, base) || exact.relativePath
  }

  const byStored = files.find((item) => normalizePath(item.storedPath).toLowerCase() === normalized.toLowerCase())
  if (byStored) {
    if (mode === 'mapped') {
      return normalizePath(byStored.storedPath) || toStoredPath(byStored.relativePath, byStored.mappingPath)
    }
    if (mode === 'engine') {
      return normalizePath(byStored.enginePath) || toEnginePath(byStored.relativePath, byStored.mappingPath)
    }
    return getRelativePathWithinBase(byStored, base) || byStored.relativePath
  }

  const byEngine = files.find((item) => normalizePath(item.enginePath).toLowerCase() === normalized.toLowerCase())
  if (byEngine) {
    if (mode === 'mapped') {
      return normalizePath(byEngine.storedPath) || toStoredPath(byEngine.relativePath, byEngine.mappingPath)
    }
    if (mode === 'engine') {
      return normalizePath(byEngine.enginePath) || toEnginePath(byEngine.relativePath, byEngine.mappingPath)
    }
    return getRelativePathWithinBase(byEngine, base) || byEngine.relativePath
  }

  if (mode === 'mapped' || mode === 'engine') {
    if (base && startsWithPath(normalized, base.folderPath)) {
      const relative = normalized.slice(base.folderPath.length).replace(/^\/+/, '')
      return mode === 'mapped'
        ? toStoredPath(relative, base.mappingPath)
        : toEnginePath(relative, base.mappingPath)
    }
    const parsed = resolveRelativePathFromVirtualPath(category, normalized)
    if (parsed.relativePath) {
      return mode === 'mapped'
        ? joinSegments(parsed.mappingPath, parsed.relativePath) || parsed.relativePath
        : toEnginePath(parsed.relativePath, parsed.mappingPath)
    }
    const fallbackMapped = parseMappedStoredPath(category, normalized)
    if (fallbackMapped) {
      return mode === 'mapped'
        ? joinSegments(fallbackMapped.mappingPath, fallbackMapped.relativePath) || fallbackMapped.relativePath
        : toEnginePath(fallbackMapped.relativePath, fallbackMapped.mappingPath)
    }
    const fallbackEngine = parseEnginePath(category, normalized)
    if (fallbackEngine) {
      return mode === 'mapped'
        ? joinSegments(fallbackEngine.mappingPath, fallbackEngine.relativePath) || fallbackEngine.relativePath
        : toEnginePath(fallbackEngine.relativePath, fallbackEngine.mappingPath)
    }
  }

  const byRelative = files.find((item) => normalizePath(item.relativePath).toLowerCase() === normalized.toLowerCase())
  if (byRelative) {
    if (mode === 'mapped') {
      return normalizePath(byRelative.storedPath) || toStoredPath(byRelative.relativePath, byRelative.mappingPath)
    }
    if (mode === 'engine') {
      return normalizePath(byRelative.enginePath) || toEnginePath(byRelative.relativePath, byRelative.mappingPath)
    }
    return getRelativePathWithinBase(byRelative, base) || byRelative.relativePath
  }

  return normalized
}

export function resolveRelativePathFromDisplayPath(
  root: DataFileMap | null | undefined,
  category: ResourceCategoryKey,
  displayPath: string,
  baseFolderPath?: string,
): string {
  const normalized = stripCategoryLabelPrefix(category, displayPath)
  if (!normalized) return ''
  const meta = findResourceFileMetaByStoredPath(root, category, normalized, baseFolderPath)
  const base = resolveBaseFolderMeta(category, baseFolderPath)
  if (meta) {
    return getRelativePathWithinBase(meta, base) || meta.relativePath
  }
  if (base && startsWithPath(normalized, base.folderPath)) {
    return normalizePath(normalized.slice(base.folderPath.length).replace(/^\/+/, ''))
  }
  return normalized
}

/**
 * 按资源管理器子文件夹优先规则矫正资源路径。
 * @param root 资源树根节点。
 * @param category 资源分类。
 * @param storedPath 当前保存或展示的路径。
 * @param baseFolderPath 基准目录，例如 `图片/UI` 或 `图片/button`。
 * @param preferredSubfolder 优先命中的子目录，例如 `商城`。
 * @returns 基于基准目录的相对路径；若优先子目录存在同名文件，则返回该子目录路径。
 */
export function resolvePreferredSubfolderRelativePath(
  root: DataFileMap | null | undefined,
  category: ResourceCategoryKey,
  storedPath: string,
  baseFolderPath: string,
  preferredSubfolder: string,
): string {
  const currentPath = resolveRelativePathFromDisplayPath(root, category, storedPath, baseFolderPath)
  const fileName = normalizePath(currentPath).split('/').filter(Boolean).pop() ?? ''
  if (!fileName) return currentPath
  const preferredPath = joinSegments(preferredSubfolder, fileName)
  const preferredMeta = findResourceFileMetaByStoredPath(root, category, preferredPath, baseFolderPath)
  if (!preferredMeta) return currentPath
  const base = resolveBaseFolderMeta(category, baseFolderPath)
  return getRelativePathWithinBase(preferredMeta, base) || preferredMeta.relativePath
}

export function resolveResourceUrlFromFileName(
  root: DataFileMap | null | undefined,
  category: ResourceCategoryKey,
  storedPath: string,
  previewOverrides?: Record<string, string>,
  baseFolderPath?: string,
): string {
  const meta = findResourceFileMetaByStoredPath(root, category, storedPath, baseFolderPath)
  if (!meta?.resourceUrl) return ''
  return previewOverrides?.[meta.resourceUrl] ?? meta.resourceUrl
}
